// assets/mikmak-script.js
//
// Single, self-healing loader for the MikMak "where to buy" tag.
//
// Why this exists: the MikMak tag (tag.min.js) occasionally
// executes but never completes its own init - it sets window.swnTagLoaded,
// then something in its start-up path throws (it swallows the error with one
// console.error and never retries). When that happens the widget loader
// (widgets.swaven.com/awe/1/loader.js) is never fetched and no
// [data-mm-wtbid] button on the page is ever bound. Re-running the tag a
// moment later succeeds, so this file:
//
//   1. Loads the tag exactly once (guards against double-injection).
//   2. Health-checks the tag after it loads and re-runs it if its config
//      never arrived (bounded retries with back-off).
//   3. Catches clicks on any "Find near you" button that MikMak hasn't bound
//      yet (tag still loading, tag stalled, or button re-rendered/unloaded),
//      triggers recovery, and replays the click once the button is bound.
//   4. Detects copies of already-bound buttons (a node inserted with MikMak's
//      classes but no live listener) and strips the stale markers so the
//      loader's 300ms scan re-binds them. Moves of the same node are ignored.
//
// Debug logging: add ?mikmak_debug=1 to the URL or set window.MIKMAK_DEBUG = true.

(function () {
  'use strict';

  var APP_ID = '67f04af5c56df30f02291cdb';
  var TAG_SRC = 'https://wtb-tag.mikmak.ai/scripts/' + APP_ID + '/tag.min.js';
  var SCRIPT_ID = 'mikmak-tag-loader';
  var BUTTON_SELECTOR = '[data-mm-wtbid]';
  var MAX_ATTEMPTS = 3;            // total tag executions we will try
  var HEALTH_TIMEOUT = 1250;        // ms after tag load to expect its config (normally ~50ms)
  var CLICK_STALL_GRACE = 500;      // ms after tag load: a click with still no config = stalled
  var RETRY_BACKOFF = 1500;         // ms, multiplied by attempt number
  var CLICK_REPLAY_TIMEOUT = 10000; // ms to wait for a button to become bound

  // Classes / attributes the MikMak widget loader puts on bound elements.
  var MM_CLASSES = ['mm-cre', 'swn-bound', 'swn-awe-btn-enabled', 'swn-awe-btn-disabled'];
  var MM_ATTRS = ['data-swn-idx', 'data-swn-instance'];

  var state = {
    attempts: 0,
    healthTimer: null,
    retryTimer: null,
    pending: false, // a tag execution is in flight and not yet health-checked
    tagExecutedAt: 0, // Date.now() when the current tag.min.js finished executing
    // Nodes MikMak has bound in *this* document. Used to tell a moved node
    // (keeps its listener) from a copied node (does not).
    boundNodes: typeof WeakSet === 'function' ? new WeakSet() : null,
  };

  var debugEnabled =
    !!window.MIKMAK_DEBUG || /[?&]mikmak_debug=1/.test(window.location.search);

  function log() {
    if (!debugEnabled || !window.console) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[MikMak]');
    console.info.apply(console, args);
  }

  function warn() {
    if (!window.console) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[MikMak]');
    console.warn.apply(console, args);
  }

  /* ------------------------------------------------------------------ */
  /* Data layer + tag injection                                          */
  /* ------------------------------------------------------------------ */

  function ensureDataLayer() {
    var a = (window.swnDataLayer = window.swnDataLayer || {});
    a.appId = a.appId || APP_ID;
    a.eventBuffer = a.eventBuffer || [];
    a.loadBuffer = a.loadBuffer || [];
    a.push = a.push || function (e) { a.eventBuffer.push(e); };
    a.load = a.load || function (e) { a.loadBuffer.push(e); };
    a.dnt = a.dnt != null ? a.dnt : false;
    return a;
  }

  // The tag populates these only after its config request succeeds.
  function tagIsHealthy() {
    var a = window.swnDataLayer;
    return !!(a && a.countries && typeof a.discover === 'function');
  }

  function isBound(el) {
    return el.classList.contains('swn-bound') && el.hasAttribute('data-swn-instance');
  }

  // Ask the widget loader to (re)start its [data-mm-wtbid] scan. It is a
  // no-op while the scan is already running, so this is safe to call often.
  function nudgeLoaderScan() {
    var awe = window.aweLoader;
    if (awe && typeof awe.endPageChange === 'function') {
      try { awe.endPageChange(); } catch (e) { /* ignore */ }
    }
  }

  function injectTag(reason) {
    if (state.attempts >= MAX_ATTEMPTS) {
      warn('giving up after ' + state.attempts + ' attempts (' + reason + ')');
      return false;
    }

    state.attempts += 1;
    state.pending = true;
    state.tagExecutedAt = 0;
    ensureDataLayer();

    // Allow the tag to run again: it bails out early when this flag is set.
    if (state.attempts > 1) window.swnTagLoaded = false;

    var existing = document.getElementById(SCRIPT_ID);
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

    var script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = TAG_SRC;

    script.addEventListener('load', function () {
      state.tagExecutedAt = Date.now();
      log('tag.min.js executed (attempt ' + state.attempts + ')');
      scheduleHealthCheck();
    });

    script.addEventListener('error', function () {
      state.pending = false;
      warn('tag.min.js failed to download (attempt ' + state.attempts + ')');
      scheduleRetry('download-error');
    });

    log('requesting tag (attempt ' + state.attempts + ', reason: ' + reason + ')');
    document.head.appendChild(script);
    return true;
  }

  function scheduleHealthCheck() {
    clearTimeout(state.healthTimer);
    state.healthTimer = setTimeout(function () {
      state.pending = false;
      if (tagIsHealthy()) {
        log('tag healthy: countries=' + window.swnDataLayer.countries.join(','));
        nudgeLoaderScan();
        return;
      }
      warn('tag executed but never initialised (no config after ' + HEALTH_TIMEOUT + 'ms)');
      scheduleRetry('init-stalled');
    }, HEALTH_TIMEOUT);
  }

  function scheduleRetry(reason) {
    if (state.retryTimer || state.attempts >= MAX_ATTEMPTS) return;
    state.retryTimer = setTimeout(function () {
      state.retryTimer = null;
      injectTag(reason);
    }, RETRY_BACKOFF * state.attempts);
  }

  // Public hook so other code (or the console) can force a recovery pass.
  window.mikmakRecover = function (reason) {
    if (tagIsHealthy() || state.pending) return false;
    clearTimeout(state.retryTimer);
    state.retryTimer = null;
    return injectTag(reason || 'manual');
  };

  /* ------------------------------------------------------------------ */
  /* Click safety net                                                    */
  /* ------------------------------------------------------------------ */

  function stripMikMakMarkers(el) {
    MM_CLASSES.forEach(function (c) { el.classList.remove(c); });
    MM_ATTRS.forEach(function (a) { el.removeAttribute(a); });
  }

  function waitForBound(el, timeout) {
    return new Promise(function (resolve, reject) {
      var started = Date.now();
      (function poll() {
        if (isBound(el)) return resolve(el);
        if (Date.now() - started > timeout) return reject(new Error('timeout'));
        setTimeout(poll, 100);
      })();
    });
  }

  function onCapturedClick(event) {
    if (!event.target || typeof event.target.closest !== 'function') return;
    var btn = event.target.closest(BUTTON_SELECTOR);
    if (!btn || btn.tagName === 'DIV' || btn.tagName === 'TEMPLATE') return;

    // Healthy, bound button: let MikMak's own listener handle it.
    if (isBound(btn)) return;

    // Already replaying a click for this button.
    if (btn.hasAttribute('data-mm-pending')) {
      event.preventDefault();
      return;
    }

    warn('click on unbound Find-near-you button; recovering');
    event.preventDefault();
    event.stopPropagation();
    btn.setAttribute('data-mm-pending', '1');

    // Stale markers (e.g. an unloaded or copied button) would stop the
    // loader from re-binding it - clear them so its 300ms scan picks it up.
    stripMikMakMarkers(btn);

    // Tag never injected (clicked before DOMContentLoaded), or it stalled and
    // no retry is already queued: kick a recovery. If the tag is still
    // downloading we just wait for it - waitForBound() below covers that.
    if (!tagIsHealthy()) {
      if (state.attempts === 0) {
        injectTag('click-before-load');
      } else if (state.pending && state.tagExecutedAt &&
                 Date.now() - state.tagExecutedAt > CLICK_STALL_GRACE) {
        // The tag has executed and its config (normally ~50ms) still isn't
        // here: that's a stall. Don't make the user wait out the health
        // timer - recover now.
        clearTimeout(state.healthTimer);
        state.pending = false;
        clearTimeout(state.retryTimer);
        state.retryTimer = null;
        injectTag('click-on-stalled');
      } else if (!state.pending && !state.retryTimer) {
        window.mikmakRecover('click-on-unbound');
      }
    } else {
      nudgeLoaderScan();
    }

    waitForBound(btn, CLICK_REPLAY_TIMEOUT).then(
      function () {
        btn.removeAttribute('data-mm-pending');
        log('button bound, replaying click');
        btn.click();
      },
      function () {
        btn.removeAttribute('data-mm-pending');
        warn('button never became bound; click dropped');
      }
    );
  }

  document.addEventListener('click', onCapturedClick, true);

  /* ------------------------------------------------------------------ */
  /* Copied-node guard                                                   */
  /* ------------------------------------------------------------------ */

  function rememberBound(el) {
    if (state.boundNodes && isBound(el)) state.boundNodes.add(el);
  }

  function handleInsertedNode(node) {
    if (node.nodeType !== 1) return;
    var candidates = [];
    if (node.matches && node.matches(BUTTON_SELECTOR)) candidates.push(node);
    if (node.querySelectorAll) {
      candidates = candidates.concat(
        Array.prototype.slice.call(node.querySelectorAll(BUTTON_SELECTOR))
      );
    }
    candidates.forEach(function (el) {
      var looksBound = el.classList.contains('mm-cre') || el.classList.contains('swn-bound');
      if (!looksBound) return; // fresh markup: loader will bind it
      if (state.boundNodes && state.boundNodes.has(el)) return; // same node moved: listener intact
      log('inserted a copy of a bound button; resetting it for re-bind');
      stripMikMakMarkers(el);
    });
  }

  function startObserver() {
    if (typeof MutationObserver !== 'function' || !document.body) return;

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type === 'attributes') {
          rememberBound(m.target);
        } else if (m.type === 'childList') {
          for (var j = 0; j < m.addedNodes.length; j++) handleInsertedNode(m.addedNodes[j]);
        }
      }
    });

    // The loader sets data-swn-instance on a button when it binds it, so
    // watching that one attribute is enough to learn which nodes are live.
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-swn-instance'],
    });
  }

  /* ------------------------------------------------------------------ */
  /* Boot                                                                */
  /* ------------------------------------------------------------------ */

  function init() {
    startObserver();

    if (document.getElementById(SCRIPT_ID) || window.swnTagLoaded) {
      // Something else already injected the tag (legacy snippet, app embed).
      warn('tag already present; not injecting a second copy');
      state.attempts = 1;
      state.tagExecutedAt = Date.now();
      scheduleHealthCheck();
      return;
    }

    injectTag('page-load');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
